import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getPayments, reviewPayment, getPaymentProofSignedUrl } from '../../services/adminService';

const METHOD_LABELS = {
  bank_transfer: 'Transferencia',
  mercado_pago: 'Mercado Pago',
  stripe: 'Stripe',
  cash: 'Efectivo',
  other: 'Otro',
};

const STATUS_TABS = [
  { key: 'pending_review', label: 'Pendientes' },
  { key: 'confirmed', label: 'Confirmados' },
  { key: 'rejected', label: 'Rechazados' },
];

const formatMxn = (amount) =>
  new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(amount) || 0);

const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '';

const AdminPaymentsPage = () => {
  const { session } = useAuth();
  const [status, setStatus] = useState('pending_review');
  const [payments, setPayments] = useState([]);
  const [purchases, setPurchases] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionId, setActionId] = useState(null);

  const load = useCallback(async () => {
    if (!session?.access_token) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getPayments(session.access_token, { status, limit: 100, page: 1 });
      setPayments(data.payments || []);
      setPurchases(data.purchases || {});
    } catch (err) {
      setError(err.message || 'Error al cargar los pagos.');
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, status]);

  useEffect(() => { load(); }, [load]);

  const openProof = async (proofPath) => {
    if (!proofPath) return;
    try {
      const data = await getPaymentProofSignedUrl(session.access_token, proofPath);
      if (data?.signedUrl) window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err) {
      alert(`No se pudo abrir el comprobante: ${err.message}`);
    }
  };

  const handleConfirm = async (payment) => {
    if (!window.confirm(`¿Confirmar el pago de ${formatMxn(payment.amount)}?`)) return;
    setActionId(payment.id);
    try {
      await reviewPayment(session.access_token, payment.id, { status: 'confirmed' });
      await load();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionId(null);
    }
  };

  const submitReject = async (e) => {
    e.preventDefault();
    if (!rejectModal) return;
    if (!rejectReason.trim()) return;
    setActionId(rejectModal.id);
    try {
      await reviewPayment(session.access_token, rejectModal.id, {
        status: 'rejected',
        reject_reason: rejectReason.trim(),
      });
      setRejectModal(null);
      setRejectReason('');
      await load();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="container" style={{ padding: '2rem 0 4rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <Link to="/admin" style={{ color: '#888', textDecoration: 'none', fontSize: '0.9rem' }}>← Panel</Link>
        <h1 style={{ fontFamily: "'Gambado Sans', sans-serif", fontSize: '1.6rem', color: '#333', margin: 0 }}>
          Pagos parciales
        </h1>
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setStatus(tab.key)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '999px',
              border: '1px solid',
              borderColor: status === tab.key ? '#2ca9e1' : '#ddd',
              background: status === tab.key ? '#2ca9e1' : 'white',
              color: status === tab.key ? 'white' : '#555',
              cursor: 'pointer',
              fontSize: '0.88rem',
              fontWeight: '600',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading && <p style={{ color: '#666' }}>Cargando pagos...</p>}
      {error && <p style={{ color: '#dc3545' }}>{error}</p>}
      {!loading && !error && payments.length === 0 && (
        <p style={{ color: '#888' }}>No hay pagos en esta categoría.</p>
      )}

      {!loading && !error && payments.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {payments.map((p) => {
            const purchase = purchases[p.purchaseId] || {};
            const remaining = Math.max(0, Number(purchase.totalAmount || 0) - Number(purchase.amountPaid || 0));
            return (
              <div
                key={p.id}
                style={{
                  background: 'white',
                  border: '1px solid #eee',
                  borderRadius: '12px',
                  padding: '1rem 1.25rem',
                  display: 'flex',
                  gap: '1rem',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ flex: 1, minWidth: '260px' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: '1.1rem', color: '#333' }}>{formatMxn(p.amount)}</strong>
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>{METHOD_LABELS[p.method] || p.method}</span>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: '999px',
                      fontSize: '0.72rem',
                      fontWeight: '600',
                      background: p.status === 'confirmed' ? '#eaf7ed' : p.status === 'rejected' ? '#fdecee' : '#fff4e0',
                      color: p.status === 'confirmed' ? '#28a745' : p.status === 'rejected' ? '#dc3545' : '#e8640c',
                    }}>
                      {p.status === 'confirmed' ? 'Confirmado' : p.status === 'rejected' ? 'Rechazado' : 'Pendiente'}
                    </span>
                  </div>
                  <p style={{ margin: '0.25rem 0', color: '#666', fontSize: '0.85rem' }}>
                    {p.createdByEmail} · {formatDate(p.createdAt)}
                  </p>
                  <p style={{ margin: '0.25rem 0', color: '#333', fontSize: '0.88rem' }}>
                    Pedido <Link to={`/admin/orders/${p.purchaseId}`} style={{ color: '#2ca9e1' }}>#{String(p.purchaseId).slice(-8)}</Link>
                    {purchase.customerName && <> · {purchase.customerName}</>}
                  </p>
                  {purchase.totalAmount > 0 && (
                    <p style={{ margin: '0.25rem 0', color: '#666', fontSize: '0.82rem' }}>
                      Pagado: {formatMxn(purchase.amountPaid)} de {formatMxn(purchase.totalAmount)} · Faltan {formatMxn(remaining)}
                    </p>
                  )}
                  {p.reference && (
                    <p style={{ margin: '0.25rem 0', color: '#666', fontSize: '0.82rem' }}>Ref: {p.reference}</p>
                  )}
                  {p.rejectReason && (
                    <p style={{ margin: '0.25rem 0', color: '#dc3545', fontSize: '0.82rem' }}>
                      Motivo de rechazo: {p.rejectReason}
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                  {p.proofUrl && (
                    <button
                      type="button"
                      onClick={() => openProof(p.proofUrl)}
                      style={{
                        padding: '0.5rem 0.9rem',
                        background: 'none',
                        border: '1px solid #2ca9e1',
                        color: '#2ca9e1',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                      }}
                    >
                      Ver comprobante
                    </button>
                  )}
                  {p.status === 'pending_review' && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleConfirm(p)}
                        disabled={actionId === p.id}
                        style={{
                          padding: '0.5rem 0.9rem',
                          background: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: actionId === p.id ? 'wait' : 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                        }}
                      >
                        Confirmar
                      </button>
                      <button
                        type="button"
                        onClick={() => { setRejectModal(p); setRejectReason(''); }}
                        disabled={actionId === p.id}
                        style={{
                          padding: '0.5rem 0.9rem',
                          background: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: actionId === p.id ? 'wait' : 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                        }}
                      >
                        Rechazar
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {rejectModal && (
        <div
          onClick={() => actionId === null && setRejectModal(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
          }}
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitReject}
            style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '420px' }}
          >
            <h3 style={{ margin: '0 0 0.5rem' }}>Rechazar pago</h3>
            <p style={{ margin: '0 0 1rem', color: '#666', fontSize: '0.88rem' }}>
              {formatMxn(rejectModal.amount)} · {rejectModal.createdByEmail}
            </p>
            <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.35rem' }}>
              Motivo del rechazo
            </label>
            <textarea
              rows={3}
              required
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              maxLength={500}
              placeholder="Ej. El comprobante no corresponde al monto declarado."
              style={{ width: '100%', padding: '0.6rem', border: '1px solid #ddd', borderRadius: '8px', fontSize: '0.9rem', boxSizing: 'border-box', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button
                type="button"
                onClick={() => setRejectModal(null)}
                disabled={actionId !== null}
                style={{ padding: '0.55rem 1rem', background: '#f0f0f0', color: '#333', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={actionId !== null || !rejectReason.trim()}
                style={{
                  padding: '0.55rem 1rem',
                  background: actionId !== null || !rejectReason.trim() ? '#f5a3aa' : '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: actionId !== null ? 'wait' : 'pointer',
                  fontWeight: '600',
                }}
              >
                {actionId !== null ? 'Enviando...' : 'Rechazar'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminPaymentsPage;
